import { PlantHalt } from '../../../../../types/plantHalts'
import { isLineDisabledHalt } from './config'
import { haltSeconds } from './durations'

export interface CauseTotal {
  key: string
  label: string
  seconds: number
  count: number
}

export interface CauseTotals {
  /** Cause dei fermi veri, per durata decrescente. */
  causes: CauseTotal[]
  /** Linea disabilitata, tenuta fuori dal totale come nei totali per linea; null se assente. */
  disabled: CauseTotal | null
  totalSeconds: number
  totalCount: number
}

/**
 * Etichetta della causa con la stessa regola della colonna Causa della tabella fermi: il
 * dettaglio si mostra solo se il backend lo segnala con `showDetailInTable`. Se la causa non
 * ha nome - per esempio un tipo che l'API non sa ancora descrivere - si mostra la chiave, per
 * non lasciare una riga senza etichetta.
 */
const causeLabel = (halt: PlantHalt): string => {
  const cause = halt.cause
  if (!cause || !cause.name) {
    return halt.causeKey
  }
  return cause.showDetailInTable && cause.detail ? `${cause.name} — ${cause.detail}` : cause.name
}

const addHalt = (total: CauseTotal | undefined, halt: PlantHalt): CauseTotal => ({
  key: halt.causeKey,
  label: total ? total.label : causeLabel(halt),
  seconds: (total ? total.seconds : 0) + haltSeconds(halt),
  count: (total ? total.count : 0) + 1,
})

/**
 * Totali per causa dei fermi passati, raggruppati per `causeKey`.
 *
 * L'elenco è dinamico: compaiono solo le cause presenti nei fermi, quindi una causa nuova
 * non richiede di aggiornare nulla qui. I fermi sono gli stessi dei totali per linea, con la
 * stessa esclusione della linea disabilitata: "Totale cause" e "Totale linee" coincidono.
 */
export const totalsByCause = (halts: PlantHalt[]): CauseTotals => {
  const byKey: { [key: string]: CauseTotal } = {}
  let disabled: CauseTotal | undefined

  halts.forEach(halt => {
    if (isLineDisabledHalt(halt)) {
      disabled = addHalt(disabled, halt)
    } else {
      byKey[halt.causeKey] = addHalt(byKey[halt.causeKey], halt)
    }
  })

  const causes = Object.keys(byKey)
    .map(key => byKey[key])
    .sort((a, b) => b.seconds - a.seconds || b.count - a.count || a.label.localeCompare(b.label))

  return {
    causes,
    disabled: disabled || null,
    totalSeconds: causes.reduce((acc, cause) => acc + cause.seconds, 0),
    totalCount: causes.reduce((acc, cause) => acc + cause.count, 0),
  }
}
